<script setup lang="ts">
import type { RecuFiscalPays } from "@donoor/utils";
import { coutAuConditionnel, tauxEconomieMaxDuRegime, tauxReductionDuRegime, useRegimeFiscal } from "~/composables/useRegimeFiscal";
const props = withDefaults(defineProps<{
  /** Montant du DON seul (hors soutien Donoor et frais couverts). */
  finalAmount: number;
  formatAmount: (amount: number) => string;
  isSubscription?: boolean;
}>(), {
  isSubscription: false,
});

// Réduction d'impôt (art. 200 CGI) : 66 % du DON SEUL. `finalAmount` est le
// montant du don, hors soutien Donoor et hors frais couverts — ni l'un ni
// l'autre ne figurent sur le reçu fiscal, ils n'entrent donc pas dans le calcul.
// Taux 75 % (org flaggée `fiscal_receipts_rate_75`) non transmis au site : on
// reste à 66 %, ce qui ne promet jamais plus que la réalité.
//
// Belgique : 30 % (attestation annuelle). Luxembourg et Pays-Bas : déduction
// du revenu, l'économie dépend du revenu du donateur → même phrase qu'en
// France au taux le plus favorable, la condition juste en dessous en petit
// (Karim, 01/11/2026 : « la phrase en dessous est là pour préciser »).
// Allemagne (03/11/2026) : déduction du revenu aussi (au mieux 47,48 %), mais
// « peut ne vous coûter que », jamais « ne vous coûte que ».
const regime = useRegimeFiscal();
const taux = computed(() => tauxReductionDuRegime(regime.value));
const tauxMax = computed(() => (taux.value === null ? tauxEconomieMaxDuRegime(regime.value) : null));
const coutApresReduction = computed(() => {
  const t = taux.value ?? tauxMax.value;
  return props.finalAmount > 0 && t !== null ? Math.round(props.finalAmount * (1 - t) * 100) / 100 : null;
});
// Un texte par régime : un nouveau pays oublié ici ne compile pas.
const TITRES_SANS_MONTANT: Record<RecuFiscalPays, string> = {
  cerfa: "don.optionsGroupe.fiscalTitre",
  be_281_71: "recusPays.fiscalTitreBe",
  lu_art112: "recusPays.fiscalTitreLu",
  nl_anbi: "recusPays.fiscalTitreNl",
  de_zuwendung: "recusPays.fiscalTitreDe",
};
const EXPLICATIONS: Record<RecuFiscalPays, string> = {
  cerfa: "don.optionsGroupe.fiscalExplication",
  be_281_71: "recusPays.fiscalExplicationBe",
  lu_art112: "recusPays.fiscalExplicationLu",
  nl_anbi: "recusPays.fiscalExplicationNl",
  de_zuwendung: "recusPays.fiscalExplicationDe",
};
const titreSansMontant = computed(() => TITRES_SANS_MONTANT[regime.value] ?? TITRES_SANS_MONTANT.cerfa);
const explication = computed(() => EXPLICATIONS[regime.value] ?? EXPLICATIONS.cerfa);
const titreAvecMontant = computed(() =>
  coutAuConditionnel(regime.value)
    ? props.isSubscription
      ? "recusPays.fiscalCoutTitreRecurrentDeduction"
      : "recusPays.fiscalCoutTitreDeduction"
    : props.isSubscription
      ? "don.optionsGroupe.fiscalCoutTitreRecurrent"
      : "don.optionsGroupe.fiscalCoutTitre",
);
</script>

<!-- Encart réduction d'impôt (noir plein) : coût réel du don après réduction
     d'impôt. Racine unique → la classe passée par le parent s'applique. -->
<template>
  <div class="rounded-xl bg-gray-950 px-4 py-3.5 text-white">
    <p class="text-[15px] font-semibold leading-snug">
      <i18n-t
        v-if="coutApresReduction !== null"
        :keypath="titreAvecMontant"
        tag="span"
      >
        <template #montant>
          <span class="whitespace-nowrap">{{ formatAmount(finalAmount) }}</span>
        </template>
        <template #cout>
          <span class="whitespace-nowrap font-extrabold">{{ formatAmount(coutApresReduction) }}</span>
        </template>
      </i18n-t>
      <template v-else>{{ $t(titreSansMontant) }}</template>
    </p>
    <p class="mt-1 text-[12.5px] text-gray-300">
      {{ $t(explication) }}
    </p>
  </div>
</template>
